import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import {
  useInitialDataLoader,
  useListingStore,
} from '@brightmls/nextgen.lae.flc';
import { useService } from '../../contexts/ServiceContext';
import ResponsiveLayout from '../../components/common/ResponsiveLayout';
import Loader from '../../components/common/loader';
import {
  DeniedEditState,
  RestrictionType,
  useListingRestrictions,
  WarningEditState,
} from '../../components/edit/hooks/useListingRestrictions';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../flags';
import { useManageListingsNavigation } from '../../hooks/useManageListingsNavigation';
import {
  MediaJobsProvider,
  type MediaJob,
} from '../../contexts/MediaJobsContext';
import { MediaResource } from '@brightmls/nextgen.lae.mediacenter';
import { useAuth } from '../../contexts/AuthContext';
import { MediaJobOrchestrator } from './media/MediaJobOrchestrator';

interface EditRouteLoaderProps {
  children: React.ReactNode;
}

interface ApiError {
  message?: string;
  error?: string;
}

const LoadingState = () => (
  <Loader
    text={'Fetching listing details...'}
    mode={'determinate'}
    showHeaderFooter={false}
    speed={4.0}
  />
);

const ErrorState = ({
  error,
  onReturn,
}: {
  error: ApiError;
  onReturn: () => void;
}) => (
  <ResponsiveLayout>
    <Box sx={{ textAlign: 'center', maxWidth: '600px', p: 3 }}>
      <Typography variant="h4" color="error" gutterBottom>
        Error Loading Listing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {error.message || error.error || 'An unexpected error occurred'}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={onReturn}
        sx={{ mt: 2 }}
      >
        Return to Overview
      </Button>
    </Box>
  </ResponsiveLayout>
);

// Inner component that can use FLC hooks
const LoadedContent: React.FC<{
  listingData: any;
  children: React.ReactNode;
}> = ({ listingData, children }) => {
  const { loadInitialData } = useInitialDataLoader();
  const { isViewOnly } = useAuth();
  const { hasFeature } = useFeatureFlags();
  const hasLoadedRef = useRef(false);

  const isMediaV2 = hasFeature(FeatureFlags.NGW_MEDIA_V2);

  useEffect(() => {
    if (listingData && !hasLoadedRef.current) {
      hasLoadedRef.current = true;

      // Load the listing data using FLC's loadInitialData
      loadInitialData(listingData, {
        clearAll: true, // Clear all resources, not just Property
        preserveKey: false, // Don't preserve any existing listing key
      });

      // Lock the form for view-only users
      if (isViewOnly()) {
        useListingStore.setState({ locked: true });
      }

      // Signal that listing data is ready — rAF ensures React has painted the form
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('listing:data-loaded'));
      });
    }
  }, [listingData, loadInitialData, isViewOnly]);

  // Extract MediaJobs from listing data (from get-listing response)
  const mediaJobs: MediaJob[] = listingData?.MediaJobs ?? [];
  const media: MediaResource[] = listingData?.Media ?? [];

  return (
    <MediaJobsProvider jobs={mediaJobs} media={media}>
      {isMediaV2 && <MediaJobOrchestrator />}
      {children}
    </MediaJobsProvider>
  );
};

export const EditRouteLoader: React.FC<EditRouteLoaderProps> = ({
  children,
}) => {
  const { listingKey } = useParams<{ listingKey: string }>();
  const { getListing } = useService();
  const { hasFeature } = useFeatureFlags();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [listingData, setListingData] = useState<any>(null);
  const hasLoadedRef = useRef(false);
  const loadingPromiseRef = useRef<Promise<void> | null>(null);
  const { setListing, isEditRestricted } = useListingRestrictions();
  const { back } = useManageListingsNavigation();

  useEffect(() => {
    const fetchListing = async () => {
      if (listingKey && !hasLoadedRef.current) {
        setIsLoading(true);
        setError(null);

        if (!loadingPromiseRef.current) {
          const isMediaV2 = hasFeature(FeatureFlags.NGW_MEDIA_V2);
          loadingPromiseRef.current = getListing(listingKey, {
            mediaV2: isMediaV2,
          })
            .then((data) => {
              hasLoadedRef.current = true;
              setListingData(data);
              setListing(data); // For permissions
              setIsLoading(false);
              loadingPromiseRef.current = null;
              // Don't reset scroll when navigating to a hash fragment
              if (!window.location.hash) {
                window.scrollTo(0, 0);
              }
            })
            .catch((error: ApiError) => {
              setError(error);
              setIsLoading(false);
              loadingPromiseRef.current = null;
            });
        }
        await loadingPromiseRef.current;
      } else {
        setIsLoading(false);
      }
    };

    fetchListing();
  }, [listingKey, getListing]);

  if (isEditRestricted(RestrictionType.DENIED)) {
    return <DeniedEditState onClick={back} />;
  }

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onReturn={back} />;
  }

  // LoadedContent will handle loading the data into FLC
  return (
    <LoadedContent listingData={listingData}>
      {isEditRestricted(RestrictionType.WARNING) && (
        <WarningEditState
          onAccept={() => {
            /* Perform some action on Accept */
          }}
          onDenied={back}
        />
      )}
      {children}
    </LoadedContent>
  );
};
